import React, { useEffect } from 'react';
import { useScreenInit } from '../../useScreenInit';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
const checklist = ['Filter cleaning and replacement', 'Refrigerant pressure checks', 'Electrical connection inspection', 'Drainage and condensate checks', 'Performance benchmarking', 'Detailed written report with photos'];
const contracts = [{
  title: 'Annual',
  visits: '1 visit per year',
  desc: 'Ideal for small commercial systems and single-unit setups requiring basic compliance and upkeep.'
}, {
  title: 'Bi-annual',
  visits: '2 visits per year',
  desc: 'Recommended for medium-sized offices and retail spaces to ensure year-round reliability.'
}, {
  title: 'Quarterly',
  visits: '4 visits per year',
  desc: 'Essential for large installations, server rooms, and high-demand hospitality environments.'
}];
export function Maintenance() {
  const screenInit = useScreenInit();
  useEffect(() => {
    document.title = 'Commercial AC Maintenance | AccuTemp';
  }, []);
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-2371">
      {/* Hero */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-2372">
        <div className="container mx-auto px-6" data-id="element-2373">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-2374">
            <div className="lg:col-span-7" data-id="element-2375">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-2376">
                Services / Maintenance
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark" data-id="element-2377">
                Proactive commercial AC maintenance
              </motion.h1>
            </div>
            <div className="lg:col-span-5 lg:pt-12" data-id="element-2378">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-2379">
                Detailed reporting. Flexible contracts. In-house engineers who
                know your system. Maintenance that protects your warranty, your
                compliance, and your budget.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Why maintenance matters */}
      <section className="bg-white py-24" data-id="element-2380">
        <div className="container mx-auto px-6" data-id="element-2381">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-2382">
            Why regular servicing matters
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-2383">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-[#E8F0F8] p-10 rounded-3xl text-center" data-id="element-2384">
              <div className="text-4xl md:text-5xl font-medium text-accent mb-4" data-id="element-2385">
                Up to 40%
              </div>
              <p className="text-primary-dark/70" data-id="element-2386">
                energy waste from poorly maintained systems
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-[#E8F0F8] p-10 rounded-3xl text-center" data-id="element-2387">
              <div className="text-4xl md:text-5xl font-medium text-accent mb-4" data-id="element-2388">
                £5,000
              </div>
              <p className="text-primary-dark/70" data-id="element-2389">
                potential fine per system for TM44 non-compliance
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-[#E8F0F8] p-10 rounded-3xl text-center" data-id="element-2390">
              <div className="text-4xl md:text-5xl font-medium text-accent mb-4" data-id="element-2391">
                16 years
              </div>
              <p className="text-primary-dark/70" data-id="element-2392">
                average lifespan of a properly maintained AccuTemp installation
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is included */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-2393">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-2394">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2395">
            What is included in a service visit
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-2396">
            {checklist.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="flex items-center gap-4 bg-white p-6 rounded-2xl shadow-sm" data-id="element-2397">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0" data-id="element-2398" />
                <span className="font-medium" data-id="element-2399">{item}</span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Service reports */}
      <section className="bg-white py-24" data-id="element-2400">
        <div className="container mx-auto px-6 max-w-3xl text-center" data-id="element-2401">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-6" data-id="element-2402">
            Reports you can actually read
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-2403">
            Our service reports are written for humans, not filing cabinets. We
            do not use generic tick-box forms. You will receive clear findings,
            prioritised recommendations, and photo evidence so you know exactly
            what was found, what was done, and what to plan for in your budget.
          </motion.p>
        </div>
      </section>

      {/* Contract options */}
      <section className="bg-[#D4DDE8] py-24" data-id="element-2404">
        <div className="container mx-auto px-6" data-id="element-2405">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-2406">
            Flexible maintenance contracts
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-2407">
            {contracts.map((contract, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-10 rounded-3xl shadow-sm border border-primary-dark/5" data-id="element-2408">
                <h3 className="text-2xl font-serif mb-2" data-id="element-2409">{contract.title}</h3>
                <div className="text-accent font-medium mb-6" data-id="element-2410">
                  {contract.visits}
                </div>
                <p className="text-primary-dark/70 leading-relaxed" data-id="element-2411">
                  {contract.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Systems we maintain */}
      <section className="bg-white py-24 border-b border-primary-dark/5" data-id="element-2412">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-2413">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-8" data-id="element-2414">
            Systems we maintain
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-primary-dark/70 leading-relaxed mb-10" data-id="element-2415">
            Our engineers are trained to service and repair all major commercial
            air conditioning systems, regardless of who installed them.
          </motion.p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2416">
            <Link to="/services/ducted" className="bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-3 rounded-full font-medium transition-colors" data-id="element-2417">
              Ducted systems
            </Link>
            <Link to="/services/cassette" className="bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-3 rounded-full font-medium transition-colors" data-id="element-2418">
              Cassette systems
            </Link>
            <Link to="/services/wall-mounted" className="bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-3 rounded-full font-medium transition-colors" data-id="element-2419">
              Wall mounted systems
            </Link>
          </div>
        </div>
      </section>

      {/* Warranty */}
      <section className="bg-white py-24" data-id="element-2420">
        <div className="container mx-auto px-6 max-w-3xl text-center" data-id="element-2421">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-6" data-id="element-2422">
            Protect your warranty
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-2423">
            Skipping scheduled services or failing to maintain proper F-Gas
            records can instantly void your manufacturer warranties. AccuTemp
            maintenance contracts ensure you stay fully covered, compliant, and
            protected against unexpected repair costs.
          </motion.p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-dark py-24 text-center" data-id="element-2424">
        <div className="container mx-auto px-6" data-id="element-2425">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-2426">
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-10" data-id="element-2427">
              Book your first service
            </h2>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2428">
              Contact our team
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2429" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}